/**
 * icons — zentrales Icon-Wrapper-Modul (Design-Identität v5 „Kontor", §8.2).
 *
 * Einziger Ort, an dem `lucide-react`-Glyphen importiert werden. Alle
 * Konsumenten (Navigation, Layout, Menüs, …) importieren AUSSCHLIESSLICH von
 * hier — nie direkt von `lucide-react` oder `react-icons`. Gründe (§8.2):
 * (1) EIN Mock-Ziel für Tests statt ≥25 einzeln gemockter `react-icons/fa`-
 *     Importe. (2) Größen-Semantik: FA skaliert mit `font-size` (1em), Lucide
 *     ist px-fix — dieser Wrapper zieht die Default-Größe gerade. (3) Lucide-
 *     Glyphen-Umbenennungen treffen nur diese eine Datei.
 *
 * `react-icons` bleibt bis zum Abschluss-Sweep (M-D5-4) parallel installiert
 * (Koexistenz für noch nicht migrierte Dateien) — dieses Modul ist NUR für
 * bereits migrierte Konsumenten.
 *
 * @module icons
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  Ban,
  Bell,
  BellOff,
  Boxes,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  Camera,
  Car,
  ChartPie,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CircleHelp,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Compass,
  Copy,
  CreditCard,
  Crown,
  Download,
  Ellipsis,
  Euro,
  ExternalLink,
  Eye,
  EyeOff,
  FileArchive,
  FileDown,
  FileInput,
  FileSignature,
  FileSpreadsheet,
  FileText,
  FileUp,
  Folder,
  FolderOpen,
  Gem,
  Globe,
  Handshake,
  Hourglass,
  House,
  Image,
  Inbox,
  Info,
  KeyRound,
  Landmark,
  Layers,
  LayoutGrid,
  Lightbulb,
  Link,
  List,
  ListChecks,
  ListTodo,
  LoaderCircle,
  Lock,
  LogOut,
  Mail,
  MailOpen,
  Map,
  MapPin,
  MessageCircle,
  MessagesSquare,
  Minus,
  PackageOpen,
  Palette,
  Pencil,
  Percent,
  Phone,
  PlaneTakeoff,
  Plus,
  Printer,
  QrCode,
  Receipt,
  RefreshCw,
  Rocket,
  Route,
  Save,
  Scale,
  ScrollText,
  Search,
  Send,
  Settings,
  ShieldCheck,
  SkipForward,
  Sparkles,
  Moon,
  Sun,
  Table,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Type,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Undo2,
  Upload,
  UserCog,
  UserPlus,
  UserRound,
  Users,
  X,
} from 'lucide-react';

/** Basis-Props aller FaeBill-Icons (§8.2 Vertrag). */
export type FbIconProps = {
  className?: string;
  size?: number;
  strokeWidth?: number;
  'aria-hidden'?: boolean;
  'aria-label'?: string;
  role?: string;
};

/** Ein FaeBill-Icon ist ein React-FC mit gebundenen Lucide-Defaults. */
export type FbIcon = React.FC<FbIconProps>;

/** UI-Default-Größe (16px-Raster, §4/§8.2). */
const DEFAULT_SIZE = 16;
/** Haarlinien-Strichstärke passend zur Border-Sprache (§4). */
const DEFAULT_STROKE_WIDTH = 1.75;
/** Icons sind standardmäßig dekorativ — Text/Label trägt die Semantik. */
const DEFAULT_ARIA_HIDDEN = true;

/**
 * Bindet Defaults an eine Lucide-Glyphe und liefert ein `FbIcon`.
 * Konsumenten können `size`/`strokeWidth`/`className`/`aria-hidden` weiterhin
 * pro Einsatzstelle überschreiben (z. B. 14px in Tabellen-Inline-Kontexten,
 * 40-48px + strokeWidth 1.25 in Empty-States, §8.2 Punkt 2).
 * `aria-label`/`role` für semantische (nicht-dekorative) Icons werden durchgereicht.
 */
function wrap(LucideComponent: LucideIcon): FbIcon {
  const Wrapped: FbIcon = ({
    className,
    size = DEFAULT_SIZE,
    strokeWidth = DEFAULT_STROKE_WIDTH,
    'aria-hidden': ariaHidden = DEFAULT_ARIA_HIDDEN,
    'aria-label': ariaLabel,
    role,
  }) => (
    <LucideComponent
      className={className}
      size={size}
      strokeWidth={strokeWidth}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      role={role}
    />
  );
  Wrapped.displayName = `FbIcon(${LucideComponent.displayName ?? 'Icon'})`;
  return Wrapped;
}

// --- Domänen-/Navigations-Icons (§8.3 Mapping-Tabelle) ----------------------

/** FaThLarge → LayoutGrid. Cockpit/Übersicht. */
export const IconOverview: FbIcon = wrap(LayoutGrid);
/** FaPlus → Plus. "+ Neu"-CTA. */
export const IconNew: FbIcon = wrap(Plus);
/** FaTasks → ListChecks. Prüfung-/Worklist-Einstieg. */
export const IconWorklist: FbIcon = wrap(ListChecks);
/** FaFileInvoice → FileText. Rechnungen. */
export const IconInvoice: FbIcon = wrap(FileText);
/** FaFileSignature → FileSignature. Angebote. */
export const IconOffer: FbIcon = wrap(FileSignature);
/** FaSyncAlt/FaSync → RefreshCw. Wiederkehrende Rechnungen / Aktualisieren. */
export const IconRefresh: FbIcon = wrap(RefreshCw);
/** FaClipboardList → ClipboardList. Workflows. */
export const IconWorkflow: FbIcon = wrap(ClipboardList);
/** FaInbox → Inbox. Eingänge/Posteingang. */
export const IconInbox: FbIcon = wrap(Inbox);
/** FaFileDownload → FileDown. Eingangsrechnungen (Datei-Download-Kontext). */
export const IconFileDownload: FbIcon = wrap(FileDown);
/** FaDownload → Download. Generischer Download-Button. */
export const IconDownload: FbIcon = wrap(Download);
/** FaCamera → Camera. Belegscanner. */
export const IconScan: FbIcon = wrap(Camera);
/** FaReceipt → Receipt. Ausgaben. */
export const IconExpense: FbIcon = wrap(Receipt);
/** FaListAlt → ListTodo. Offene Posten. */
export const IconOpenItems: FbIcon = wrap(ListTodo);
/** FaUniversity → Landmark. Bank-Abgleich. */
export const IconBank: FbIcon = wrap(Landmark);
/** FaPercent → Percent. USt-Voranmeldung. */
export const IconVat: FbIcon = wrap(Percent);
/** FaFileCsv → FileSpreadsheet. DATEV-Export. */
export const IconCsv: FbIcon = wrap(FileSpreadsheet);
/** FaShieldAlt → ShieldCheck. GoBD-Verfahrensdoku. */
export const IconGobd: FbIcon = wrap(ShieldCheck);
/** FaUsers → Users. Kunden/Mandanten. */
export const IconClients: FbIcon = wrap(Users);
/** FaBoxes → Boxes. Produkte. */
export const IconProducts: FbIcon = wrap(Boxes);
/** FaUserTie → UserCog. Steuerberater-Portal. */
export const IconAdvisor: FbIcon = wrap(UserCog);
/** FaBriefcase → Briefcase. Kanzlei-/Portal-Sektion. */
export const IconPortal: FbIcon = wrap(Briefcase);
/** FaUser → UserRound. Konto/Profil, Avatar-Fallback. */
export const IconUser: FbIcon = wrap(UserRound);
/** FaPalette → Palette. PDF-Design/Theme. */
export const IconTheme: FbIcon = wrap(Palette);
/** FaCreditCard → CreditCard. Abonnement/Billing. */
export const IconBilling: FbIcon = wrap(CreditCard);
/** FaLayerGroup → Layers. DATEV-Sammelexport (Advisor-Top-Link). */
export const IconAdvisorDatev: FbIcon = wrap(Layers);
/** FaClipboardCheck → ClipboardCheck. Advisor-Cockpit (Top-Link). */
export const IconAdvisorCockpit: FbIcon = wrap(ClipboardCheck);
/** FaRegComments → MessagesSquare. Nachrichten. */
export const IconMessages: FbIcon = wrap(MessagesSquare);
/** FaSignOutAlt → LogOut. Logout. */
export const IconLogout: FbIcon = wrap(LogOut);
/** FaEnvelope → Mail. */
export const IconMail: FbIcon = wrap(Mail);
/** FaEuroSign → Euro. */
export const IconEuro: FbIcon = wrap(Euro);
/** FaBell → Bell. Benachrichtigungen an. */
export const IconNotifications: FbIcon = wrap(Bell);
/** FaBellSlash → BellOff. Benachrichtigungen aus. */
export const IconNotificationsOff: FbIcon = wrap(BellOff);
/** FaChartLine → TrendingUp. Cockpit-Trend (steigend / „up"). */
export const IconTrend: FbIcon = wrap(TrendingUp);
/** TrendingDown. Fallender Trend (z. B. DSO gesunken = besser). */
export const IconTrendDown: FbIcon = wrap(TrendingDown);
/** Minus. Unveränderter Trend („flat"). */
export const IconTrendFlat: FbIcon = wrap(Minus);
/** FaChartPie → ChartPie. Cockpit-Verteilung. */
export const IconChartPie: FbIcon = wrap(ChartPie);

// --- Status-/Feedback-Icons --------------------------------------------------

/** FaCheckCircle → CircleCheck. */
export const IconSuccess: FbIcon = wrap(CircleCheck);
/** FaRegCircle → Circle. Leerer (offener) Checklisten-Kreis. */
export const IconCircle: FbIcon = wrap(Circle);
/** FaExclamationTriangle → TriangleAlert. */
export const IconWarning: FbIcon = wrap(TriangleAlert);
/** FaExclamationCircle → CircleAlert. */
export const IconError: FbIcon = wrap(CircleAlert);
/** FaInfoCircle → Info. */
export const IconInfo: FbIcon = wrap(Info);
/** FaTimes → X. Schließen. */
export const IconClose: FbIcon = wrap(X);
/** FaTimesCircle → CircleX. Abgelehnt/Fehlgeschlagen. */
export const IconRejected: FbIcon = wrap(CircleX);
/**
 * FaSpinner → LoaderCircle. Konsument ergänzt selbst `animate-spin`
 * (Wrapper bindet keine Animations-Klasse, damit statische Einsatzstellen
 * — z. B. Icon-Kataloge — nicht ungewollt rotieren).
 */
export const IconSpinner: FbIcon = wrap(LoaderCircle);

// --- Aktions-Icons -----------------------------------------------------------

/** FaEllipsisH → Ellipsis. Zeilen-Aktionsmenü (RowActionsMenu). */
export const IconRowActions: FbIcon = wrap(Ellipsis);
/** FaTrash/FaTrashAlt → Trash2. */
export const IconDelete: FbIcon = wrap(Trash2);
/** FaPen → Pencil. */
export const IconEdit: FbIcon = wrap(Pencil);
/** FaSearch → Search. */
export const IconSearch: FbIcon = wrap(Search);
/** FaEye → Eye. */
export const IconView: FbIcon = wrap(Eye);
/** FaEyeSlash → EyeOff. */
export const IconViewOff: FbIcon = wrap(EyeOff);
/** FaPaperPlane → Send. */
export const IconSend: FbIcon = wrap(Send);
/** FaUndo → Undo2. */
export const IconUndo: FbIcon = wrap(Undo2);
/** FaLock → Lock. */
export const IconLock: FbIcon = wrap(Lock);

// --- Chevrons ----------------------------------------------------------------

/** FaChevronDown → ChevronDown. */
export const IconChevronDown: FbIcon = wrap(ChevronDown);
/** FaChevronRight → ChevronRight. */
export const IconChevronRight: FbIcon = wrap(ChevronRight);

// --- Weitere Aktions-Icons (M-D5-I1) -----------------------------------------

/** FaRegFolderOpen → FolderOpen. „Öffnen"-Zeilenaktion (UnifiedInboxTable). */
export const IconFolderOpen: FbIcon = wrap(FolderOpen);

// --- Icon-Sweep M-D5-4 (Batch 0) ---------------------------------------------

/** FaKey → KeyRound. API-Schlüssel / Sicherheits-Zugang. */
export const IconKey: FbIcon = wrap(KeyRound);
/** FaCopy → Copy. In Zwischenablage kopieren. */
export const IconCopy: FbIcon = wrap(Copy);
/** FaCheck → Check. Einzelnes Häkchen (Bestätigung). */
export const IconCheck: FbIcon = wrap(Check);
/** FaCheckDouble → CheckCheck. Doppel-Häkchen (gelesen/erledigt). */
export const IconCheckDouble: FbIcon = wrap(CheckCheck);
/** FaMinus → Minus. Entfernen/Reduzieren-Aktion. */
export const IconMinus: FbIcon = wrap(Minus);
/** FaArrowUp → ArrowUp. Sortierung/Richtung aufwärts. */
export const IconArrowUp: FbIcon = wrap(ArrowUp);
/** FaArrowDown → ArrowDown. Sortierung/Richtung abwärts. */
export const IconArrowDown: FbIcon = wrap(ArrowDown);
/** FaArrowLeft → ArrowLeft. Navigation zurück. */
export const IconArrowLeft: FbIcon = wrap(ArrowLeft);
/** FaArrowRight → ArrowRight. Navigation weiter. */
export const IconArrowRight: FbIcon = wrap(ArrowRight);
/** FaChevronLeft → ChevronLeft. Pagination/Zurück-Chevron. */
export const IconChevronLeft: FbIcon = wrap(ChevronLeft);
/** FaChevronUp → ChevronUp. Einklappen/Sortierung aufwärts. */
export const IconChevronUp: FbIcon = wrap(ChevronUp);
/** FaFileContract → ScrollText. Vertrag/AGB-Dokument. */
export const IconContract: FbIcon = wrap(ScrollText);
/** FaExternalLinkAlt → ExternalLink. Link öffnet neuen Tab/neues Ziel. */
export const IconExternalLink: FbIcon = wrap(ExternalLink);
/** FaExchangeAlt → ArrowLeftRight. Umbuchung/Austausch. */
export const IconExchange: FbIcon = wrap(ArrowLeftRight);
/** FaListUl → List. Listenansicht. */
export const IconList: FbIcon = wrap(List);
/** FaQuestionCircle → CircleHelp. Hilfe/Tooltip-Trigger. */
export const IconHelp: FbIcon = wrap(CircleHelp);
/** FaHandshake → Handshake. Partnerschaft/Vereinbarung. */
export const IconHandshake: FbIcon = wrap(Handshake);
/** FaCalculator → Calculator. Berechnung/Kalkulator. */
export const IconCalculator: FbIcon = wrap(Calculator);
/** FaPercentage → Percent. Prozent-/Rabatt-Angabe. */
export const IconPercentage: FbIcon = wrap(Percent);
/** FaUserPlus → UserPlus. Nutzer/Kontakt hinzufügen. */
export const IconUserPlus: FbIcon = wrap(UserPlus);
/** FaMagic → Sparkles. KI-/Automatik-Funktion. */
export const IconMagic: FbIcon = wrap(Sparkles);
/** FaCompass → Compass. Orientierung/Onboarding-Guide. */
export const IconCompass: FbIcon = wrap(Compass);
/** FaHome → House. Startseite/Cockpit. */
export const IconHome: FbIcon = wrap(House);
/** FaHourglassHalf → Hourglass. Wartezustand/Ablauf. */
export const IconHourglass: FbIcon = wrap(Hourglass);
/** FaImage → Image. Bild-Platzhalter/Vorschau. */
export const IconImage: FbIcon = wrap(Image);
/** FaCrown → Crown. Premium-/Ultra-Kennzeichnung. */
export const IconCrown: FbIcon = wrap(Crown);
/** FaGem → Gem. Premium-Feature-Highlight. */
export const IconGem: FbIcon = wrap(Gem);
/** FaEnvelopeOpen → MailOpen. Gelesene Nachricht. */
export const IconMailOpen: FbIcon = wrap(MailOpen);
/** FaCommentDots → MessageCircle. Chat-/Kommentarfunktion. */
export const IconCommentDots: FbIcon = wrap(MessageCircle);
/** FaFileUpload → FileUp. Datei-Upload. */
export const IconFileUpload: FbIcon = wrap(FileUp);
/** FaUpload → Upload. Generischer Upload-Button. */
export const IconUpload: FbIcon = wrap(Upload);
/** FaArchive → Archive. Archivieren-Aktion. */
export const IconArchive: FbIcon = wrap(Archive);
/** FaBoxOpen → PackageOpen. Geöffnetes Paket/Bundle. */
export const IconBoxOpen: FbIcon = wrap(PackageOpen);
/** FaFileImport → FileInput. Datei-Import. */
export const IconFileImport: FbIcon = wrap(FileInput);
/** FaFileArchive → FileArchive. ZIP-/Archiv-Datei. */
export const IconFileArchive: FbIcon = wrap(FileArchive);
/** FaLink → Link. Verknüpfung/URL. */
export const IconLink: FbIcon = wrap(Link);
/** FaBuilding → Building2. Firma/Kanzlei-Standort. */
export const IconBuilding: FbIcon = wrap(Building2);
/** FaCalendarAlt → Calendar. Datum/Termin. */
export const IconCalendar: FbIcon = wrap(Calendar);
/** FaBalanceScale → Scale. Recht/Compliance. */
export const IconScale: FbIcon = wrap(Scale);
/** FaMapMarkerAlt → MapPin. Standort/Adresse. */
export const IconMapPin: FbIcon = wrap(MapPin);
/** FaForward → SkipForward. Überspringen/Weiterleiten. */
export const IconForward: FbIcon = wrap(SkipForward);
/** FaQrcode → QrCode. QR-Code (Zahlung/Beleg). */
export const IconQrCode: FbIcon = wrap(QrCode);
/** FaLightbulb → Lightbulb. Tipp/Hinweis. */
export const IconLightbulb: FbIcon = wrap(Lightbulb);
/** FaRocket → Rocket. Start/Onboarding-Kickoff. */
export const IconRocket: FbIcon = wrap(Rocket);
/** FaMap → Map. Roadmap/Übersichtskarte. */
export const IconMap: FbIcon = wrap(Map);
/** FaCog → Settings. Einstellungen. */
export const IconSettings: FbIcon = wrap(Settings);
/** FaFilePdf → FileText (bereits importiert, kein Doppel-Import). PDF-Dokument. */
export const IconPdf: FbIcon = wrap(FileText);
/** FaPhone → Phone. Telefonnummer/Kontakt. */
export const IconPhone: FbIcon = wrap(Phone);

// --- Icon-Sweep M-D5-4 (Batch 3 — nicht in modules-ux-icon-sweep.md §1
//     abgedeckt, ergänzt hier analog zu Batch 0) --------------------------------

/** FaFont → Type. Schriftart-Auswahl (PDF-Design). */
export const IconFont: FbIcon = wrap(Type);
/** FaTable → Table. Tabellen-/Positionslayout (PDF-Design). */
export const IconTable: FbIcon = wrap(Table);
/** FaSave → Save. Speichern-Aktion. */
export const IconSave: FbIcon = wrap(Save);

// --- Icon-Sweep M-D5-4 (Batch 6 — nicht in modules-ux-icon-sweep.md §1
//     abgedeckt, ergänzt hier analog zu Batch 0/3) -----------------------------

/** FaFolder → Folder. Archiv-Tour-Schritt (geschlossener Ordner, Abgrenzung zu IconFolderOpen). */
export const IconFolder: FbIcon = wrap(Folder);
/** FaRoute → Route. Navigationspfad-Hinweis (AppTour „So geht's"). */
export const IconRoute: FbIcon = wrap(Route);

// --- Icon-Sweep M-D5-4 (Batch 8 — Rest, letzte Source-Migration) -------------

/** FaRegClock → Clock. Wartezustand „queued" (EmailStatusChip-Symbolsprache). */
export const IconClock: FbIcon = wrap(Clock);
/** FaGlobe → Globe. Webhook-Endpoint (URL/Netz-Symbol). */
export const IconGlobe: FbIcon = wrap(Globe);
/** FaToggleOn → ToggleRight. Aktiv-Zustand (Webhook-Endpoint an/aus). */
export const IconToggleOn: FbIcon = wrap(ToggleRight);
/** FaToggleOff → ToggleLeft. Inaktiv-Zustand (Webhook-Endpoint an/aus). */
export const IconToggleOff: FbIcon = wrap(ToggleLeft);
/** FaFileAlt → FileText (bereits importiert, kein Doppel-Import). Generisches Dokument (Prüfbericht). */
export const IconDocument: FbIcon = wrap(FileText);

// --- M-D5 Welle 3 (Light-Theme-Umschalter, plan-ux-d5-w3-light-flip.md §2) ---

/** Sun. Umschalter-Zustand "Hell" (Light-Theme aktiv/anwählbar). */
export const IconSun: FbIcon = wrap(Sun);
/** Moon. Umschalter-Zustand "Dunkel" (Dark-Theme aktiv/anwählbar). */
export const IconMoon: FbIcon = wrap(Moon);

// --- Status-Chip-Delta (plan-ux-status-chips-storno-inbox.md §Icon-Wrapper-Delta) ---

/** Ban. Entwertet/ungültig — Storno-Status (Rechnungen `isCancelled`) + Eingänge `discarded`. */
export const IconVoid: FbIcon = wrap(Ban);
/** CircleDot. Neu-Indikator (Ungelesen-Punkt, Messenger-Konvention) — Eingänge `new`. */
export const IconStatusNew: FbIcon = wrap(CircleDot);

// --- Reisekosten-Angleichung (plan-ux-reisekosten-align.md §1.1) ------------

/** FaPlaneDeparture → PlaneTakeoff. Dienstreisen. */
export const IconTravel: FbIcon = wrap(PlaneTakeoff);
/** FaCar → Car. Kilometergeld-Block. */
export const IconCar: FbIcon = wrap(Car);
/** FaPrint → Printer. Druckansicht-Kennzeichnung (abgerechnete Reise). */
export const IconPrint: FbIcon = wrap(Printer);
